import axios from "axios";
import { ReqMethods, ResponseStatus } from "../enums";
import env from "../config";
import { endSession, getToken } from "../auth/storage";

const BASE_URL = `${env.API_URL}`;

// The message the backend sends when a find/findOne matches nothing.
const NO_DATA = "no data found";

// Unwraps the standard { success, message, data } envelope.
// Never throws — callers branch on `error` instead of wrapping every call
// in try/catch.
function useServiceHandler() {
  return (fn) =>
    async (method, path, options = {}) => {
      try {
        const res = await fn(method, path, options);
        const body = res.data;
        const response =
          body && typeof body === "object" && "success" in body && "data" in body
            ? body.data
            : body;
        return { response, meta: body };
      } catch (err) {
        if (err.response && err.response.status === ResponseStatus.UNAUTHORIZED) {
          // The session token was refused — expired, or the user is gone.
          // Only for a signed-in session: a wrong password on the sign-in
          // page is not this.
          if (getToken()) endSession();
          return { error: err.response?.data || err };
        }
        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const serviceHandler = useServiceHandler();

  const CallService = (method, path, { query, payload, headers = {} } = {}) => {
    const url = query ? `${BASE_URL}${path}?${query}` : `${BASE_URL}${path}`;

    // The backend's Authenticate middleware reads `x-auth-token`, not
    // `Authorization` (see backend/src/lib/auth/auth.services.js).
    const token = getToken();
    const config = {
      headers: { ...(token && { "x-auth-token": `Bearer ${token}` }), ...headers },
    };

    if (method === ReqMethods.GET || method === ReqMethods.DELETE)
      return axios[method](url, config);
    return axios[method](url, payload, config);
  };

  return serviceHandler(CallService);
}

/**
 * Every backend call in the app goes through here, grouped by the router that
 * serves it (backend/src/routes/*). Each method resolves to
 * `{ response, meta }` on success or `{ error }` on failure.
 */
function useAppServices() {
  const { GET, POST, PUT, DELETE } = ReqMethods;
  const CallService = useCallService();

  const apps = {
    // GET /apps — every query key is forwarded to AppsModel.find().
    // Returns [] rather than an error when the backend reports "no data found",
    // so list screens can render an empty state instead of a failure.
    Get: async (query = "") => {
      const res = await CallService(GET, "/apps", { query });
      // The backend answers an empty collection with 400 "no data found".
      // Only that exact case is an empty list — a 500 must stay an error.
      if (res.error && res.error?.message === NO_DATA) return { response: [] };
      return res;
    },

    // GET /apps/filter — a single document via findOne().
    Filter: (query = "") => CallService(GET, "/apps/filter", { query }),

    // PUT /apps — payload must carry _id; the rest is $set.
    Update: (payload) => CallService(PUT, "/apps", { payload }),

    Create: (payload) => CallService(POST, "/apps", { payload }),

    // DELETE /apps?_id=...
    Delete: (_id) => CallService(DELETE, "/apps", { query: `_id=${_id}` }),
  };

  const auth = {
    Login: (payload) => CallService(POST, "/auth/login", { payload }),
    Register: (payload) => CallService(POST, "/auth", { payload }),
    // POST /auth/integrate_app — runs the GHL OAuth install exchange.
    IntegrateApp: (payload) => CallService(POST, "/auth/integrate_app", { payload }),
  };

  const ghl = {
    // POST /services/ghl — generic passthrough to the GHL REST API.
    Call: (payload) => CallService(POST, "/services/ghl", { payload }),
  };

  // Feature requests sent from app pages. Admin-only: these need the session.
  const featureRequests = {
    // GET /feature-requests/admin?status=&appId= — { requests, counts }
    List: (query = "") => CallService(GET, "/feature-requests/admin", { query }),
    // PUT /feature-requests/admin  { _id, status?, response? }
    Update: (payload) => CallService(PUT, "/feature-requests/admin", { payload }),
    // DELETE /feature-requests/admin?_id=...
    Delete: (_id) => CallService(DELETE, "/feature-requests/admin", { query: `_id=${_id}` }),
  };

  // Support tickets. Admin-only: these need the session.
  const support = {
    // GET /support/admin/tickets?status=&appId= — { tickets, counts, unread }
    List: (query = "") => CallService(GET, "/support/admin/tickets", { query }),
    // GET /support/admin/tickets/:id — { ticket, messages }; marks it read
    Get: (id) => CallService(GET, `/support/admin/tickets/${id}`),
    Reply: (id, message) => CallService(POST, `/support/admin/tickets/${id}/messages`, { payload: { message } }),
    Read: (id) => CallService(POST, `/support/admin/tickets/${id}/read`, { payload: {} }),
    // PUT /support/admin/tickets/:id  { status?, priority? }
    Update: (id, payload) => CallService(PUT, `/support/admin/tickets/${id}`, { payload }),
    // EventSource can't send headers, so the stream carries the token.
    streamUrl: () => `${BASE_URL}/support/admin/stream?token=${encodeURIComponent(getToken() || "")}`,
  };

  // Announcements for the notification bar on app pages. Admin-only.
  const announcements = {
    List: (query = "") => CallService(GET, "/announcements/admin", { query }),
    Create: (payload) => CallService(POST, "/announcements/admin", { payload }),
    Update: (id, payload) => CallService(PUT, `/announcements/admin/${id}`, { payload }),
    Delete: (id) => CallService(DELETE, `/announcements/admin/${id}`),
  };

  // Notifications to chosen accounts. Admin-only.
  const notifications = {
    List: (query = "") => CallService(GET, "/notifications/admin", { query }),
    // Every account with a live install, for the recipient picker.
    Accounts: () => CallService(GET, "/notifications/admin/accounts"),
    // { title, message, tone, linkLabel, linkUrl, appId, recipients: [accountKey] }
    Create: (payload) => CallService(POST, "/notifications/admin", { payload }),
    Delete: (id) => CallService(DELETE, `/notifications/admin/${id}`),
  };

  // Every install, for the Installs page. Admin-only, read-only; never
  // carries tokens, and secrets come back masked.
  const installs = {
    List: (query = "") => CallService(GET, "/app-installs/admin", { query }),
    Stats: () => CallService(GET, "/app-installs/admin/stats"),
    Get: (id) => CallService(GET, `/app-installs/admin/${id}`),
    // Plan IDs seen on an app's installs, with how many use each.
    Plans: (appId) => CallService(GET, "/app-installs/admin/plans", { query: `appId=${encodeURIComponent(appId)}` }),
  };

  // Companies, locations and users behind installs, and the analytics page.
  // Admin-only, read-only.
  const directory = {
    Companies: (query = "") => CallService(GET, "/directory/companies", { query }),
    Company: (id) => CallService(GET, `/directory/companies/${id}`),
    Locations: (query = "") => CallService(GET, "/directory/locations", { query }),
    Location: (id) => CallService(GET, `/directory/locations/${id}`),
    Users: (query = "") => CallService(GET, "/directory/users", { query }),
    User: (id) => CallService(GET, `/directory/users/${id}`),
    Analytics: (query = "") => CallService(GET, "/directory/analytics", { query }),
    // What needs acting on today, for the home page.
    Attention: () => CallService(GET, "/directory/attention"),
  };

  // The activity log and the marketplace event log. Admin-only, read-only.
  const logs = {
    Activity: (query = "") => CallService(GET, "/logs/activity", { query }),
    ActivityStats: () => CallService(GET, "/logs/activity/stats"),
    ActivityGet: (id) => CallService(GET, `/logs/activity/${id}`),
    Events: (query = "") => CallService(GET, "/logs/events", { query }),
    EventsStats: () => CallService(GET, "/logs/events/stats"),
    EventGet: (id) => CallService(GET, `/logs/events/${id}`),
  };

  // The super admin's team (admins only), and the signed-in member's own account.
  const team = {
    List: () => CallService(GET, "/team"),
    Create: (payload) => CallService(POST, "/team", { payload }),
    Update: (id, payload) => CallService(PUT, `/team/${id}`, { payload }),
    ResetPassword: (id) => CallService(POST, `/team/${id}/reset-password`),
    Remove: (id) => CallService(DELETE, `/team/${id}`),
    Me: () => CallService(GET, "/team/me"),
    ChangePassword: (payload) => CallService(PUT, "/team/me/password", { payload }),
  };

  return { apps, auth, ghl, featureRequests, support, announcements, notifications, installs, directory, logs, team };
}

export { useAppServices, useCallService, NO_DATA };
export default useAppServices;
